import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { jobTimelineAPI } from '../lib/api';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Search, Loader2, Clock, Package, FileText, AlertTriangle, 
  DollarSign, ClipboardList, Calendar, User, ChevronRight, 
  ShoppingCart, Truck, CheckCircle2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

// Format timestamp to local time
const formatLocalTime = (timestamp, formatStr) => {
  if (!timestamp) return '';
  try {
    const date = typeof timestamp === 'string' 
      ? parseISO(timestamp.endsWith('Z') ? timestamp : timestamp + 'Z')
      : timestamp;
    return format(date, formatStr);
  } catch {
    return format(new Date(timestamp), formatStr);
  }
};

// Status badge colors by event type
const STATUS_COLORS = {
  // PO statuses
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  ordered: 'bg-indigo-100 text-indigo-800',
  partially_received: 'bg-orange-100 text-orange-800',
  received: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  // Part Request statuses
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  fulfilled: 'bg-green-100 text-green-800',
  // Quarantine statuses
  active: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
};

// Event type icons and colors
const EVENT_TYPE_CONFIG = {
  purchase_order: {
    icon: ShoppingCart,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-600',
    label: 'Purchase Order',
  },
  part_request: {
    icon: ClipboardList,
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    iconColor: 'text-purple-600',
    label: 'Part Request',
  },
  quarantine: {
    icon: AlertTriangle,
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    iconColor: 'text-amber-600',
    label: 'Quarantine',
  },
};

// Format status for display
const formatStatus = (status) => {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

export const JobTimeline = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialJobNumber = searchParams.get('job') || '';
  
  const [searchQuery, setSearchQuery] = useState(initialJobNumber);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const [selectedJob, setSelectedJob] = useState(initialJobNumber);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Filter state
  const [eventTypeFilter, setEventTypeFilter] = useState('all');

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 1) {
      setSearchResults([]);
      return;
    }
    
    // Don't search if query matches the currently selected job
    if (selectedJob && searchQuery === selectedJob) {
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await jobTimelineAPI.search(searchQuery);
        setSearchResults(response.data || []);
        setShowDropdown(true);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearching(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, selectedJob]);

  // Load timeline when job is selected
  const loadTimeline = useCallback(async (jobNumber) => {
    if (!jobNumber) return;
    
    setLoading(true);
    try {
      const response = await jobTimelineAPI.getTimeline(jobNumber);
      setTimeline(response.data);
      setSearchParams({ job: jobNumber });
    } catch (error) {
      console.error('Error loading timeline:', error);
      toast.error('Failed to load job timeline');
      setTimeline(null);
    } finally {
      setLoading(false);
    }
  }, [setSearchParams]);

  // Load initial job if provided
  useEffect(() => {
    if (initialJobNumber && !timeline) {
      loadTimeline(initialJobNumber);
    }
  }, [initialJobNumber, timeline, loadTimeline]);

  // Handle job selection
  const handleSelectJob = (job) => {
    setSelectedJob(job.job_number);
    setSearchQuery(job.job_number);
    setShowDropdown(false);
    loadTimeline(job.job_number);
  };

  // Filtered events
  const filteredEvents = useMemo(() => {
    if (!timeline?.events) return [];
    if (eventTypeFilter === 'all') return timeline.events;
    return timeline.events.filter(e => e.event_type === eventTypeFilter);
  }, [timeline?.events, eventTypeFilter]);

  // Open event detail modal
  const openEventDetail = (event) => {
    setSelectedEvent(event);
    setDetailModalOpen(true);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold font-heading text-foreground flex items-center gap-2">
              <Clock className="w-7 h-7 text-blue-600" />
              Job Timeline
            </h1>
            <p className="text-muted-foreground mt-1 text-sm lg:text-base">
              View all POs, Part Requests, and related activity for a job
            </p>
          </div>
        </div>

        {/* Search Section */}
        <Card>
          <CardContent className="pt-4">
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by job number or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                  className="pl-9"
                  data-testid="job-search-input"
                />
                {searching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
              
              {/* Search dropdown */}
              {showDropdown && searchResults.length > 0 && (
                <div 
                  className="absolute z-50 w-full mt-1 bg-white border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
                  data-testid="job-search-dropdown"
                >
                  {searchResults.map((job) => (
                    <div
                      key={job.job_number}
                      className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                      onClick={() => handleSelectJob(job)}
                      data-testid={`job-option-${job.job_number}`}
                    >
                      <div className="font-medium">{job.job_number}</div>
                      {job.job_name && (
                        <div className="text-sm text-muted-foreground truncate">{job.job_name}</div>
                      )}
                      <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                        <span>{job.po_count} POs</span>
                        <span>{job.part_request_count} Part Requests</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* No results message */}
              {showDropdown && searchQuery.length >= 1 && !searching && searchResults.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                  No jobs found matching "{searchQuery}"
                </div>
              )}
            </div>
            
            {/* Click outside to close dropdown */}
            {showDropdown && (
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowDropdown(false)}
              />
            )}
          </CardContent>
        </Card>

        {/* Timeline Content */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : timeline ? (
          <>
            {/* Job Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{timeline.job_number}</div>
                  <div className="text-sm text-muted-foreground truncate">
                    {timeline.job_name || 'Job Number'}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-blue-600">{timeline.po_count}</div>
                  <div className="text-sm text-muted-foreground">Purchase Orders</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-purple-600">{timeline.part_request_count}</div>
                  <div className="text-sm text-muted-foreground">Part Requests</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(timeline.total_po_value)}
                  </div>
                  <div className="text-sm text-muted-foreground">Total PO Value</div>
                </CardContent>
              </Card>
            </div>

            {/* Event Type Filters */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={eventTypeFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEventTypeFilter('all')}
                data-testid="filter-all"
              >
                All ({timeline.total_events})
              </Button>
              <Button
                variant={eventTypeFilter === 'purchase_order' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEventTypeFilter('purchase_order')}
                data-testid="filter-po"
              >
                <ShoppingCart className="w-4 h-4 mr-1" />
                POs ({timeline.po_count})
              </Button>
              <Button
                variant={eventTypeFilter === 'part_request' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEventTypeFilter('part_request')}
                data-testid="filter-pr"
              >
                <ClipboardList className="w-4 h-4 mr-1" />
                Part Requests ({timeline.part_request_count})
              </Button>
              <Button
                variant={eventTypeFilter === 'quarantine' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEventTypeFilter('quarantine')}
                data-testid="filter-quarantine"
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                Quarantine ({timeline.quarantine_count})
              </Button>
            </div>

            {/* Timeline Events */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Timeline</CardTitle>
                <CardDescription>
                  {filteredEvents.length} events • Most recent first
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {filteredEvents.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No events found</p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
                    
                    {filteredEvents.map((event, index) => {
                      const config = EVENT_TYPE_CONFIG[event.event_type];
                      const EventIcon = config.icon;
                      
                      return (
                        <div
                          key={`${event.event_type}-${event.id}`}
                          className="relative pl-14 pr-4 py-4 hover:bg-muted/30 cursor-pointer transition-colors border-b last:border-b-0"
                          onClick={() => openEventDetail(event)}
                          data-testid={`timeline-event-${event.id}`}
                        >
                          {/* Timeline dot */}
                          <div className={`absolute left-3 top-5 w-6 h-6 rounded-full ${config.bgColor} ${config.borderColor} border-2 flex items-center justify-center`}>
                            <EventIcon className={`w-3 h-3 ${config.iconColor}`} />
                          </div>
                          
                          {/* Event content */}
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold">{event.title}</span>
                                <Badge className={STATUS_COLORS[event.status] || 'bg-gray-100'}>
                                  {formatStatus(event.status)}
                                </Badge>
                              </div>
                              <div className="text-sm text-muted-foreground mt-1">
                                {event.description}
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatLocalTime(event.timestamp, 'MMM d, yyyy h:mm a')}
                                </span>
                                {event.created_by_name && (
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {event.created_by_name}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground hidden md:block" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Search className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                Search for a job to view its timeline
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Event Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent && (
                <>
                  {React.createElement(EVENT_TYPE_CONFIG[selectedEvent.event_type].icon, {
                    className: `w-5 h-5 ${EVENT_TYPE_CONFIG[selectedEvent.event_type].iconColor}`
                  })}
                  {selectedEvent.title}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4 py-4">
              {/* Status & Type */}
              <div className="flex items-center gap-2">
                <Badge className={STATUS_COLORS[selectedEvent.status] || 'bg-gray-100'}>
                  {formatStatus(selectedEvent.status)}
                </Badge>
                <Badge variant="outline">
                  {EVENT_TYPE_CONFIG[selectedEvent.event_type].label}
                </Badge>
              </div>
              
              {/* Description */}
              <div className="text-sm text-muted-foreground">
                {selectedEvent.description}
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Created</div>
                  <div className="font-medium">
                    {formatLocalTime(selectedEvent.timestamp, 'MMM d, yyyy h:mm a')}
                  </div>
                </div>
                {selectedEvent.created_by_name && (
                  <div>
                    <div className="text-muted-foreground">Created By</div>
                    <div className="font-medium">{selectedEvent.created_by_name}</div>
                  </div>
                )}
              </div>
              
              {/* Type-specific details */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Details</h4>
                
                {selectedEvent.event_type === 'purchase_order' && (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {selectedEvent.details.po_number && (
                      <div>
                        <div className="text-muted-foreground">PO Number</div>
                        <div className="font-medium">{selectedEvent.details.po_number}</div>
                      </div>
                    )}
                    {selectedEvent.details.department_name && (
                      <div>
                        <div className="text-muted-foreground">Department</div>
                        <div className="font-medium">{selectedEvent.details.department_name}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-muted-foreground">Total Value</div>
                      <div className="font-medium">{formatCurrency(selectedEvent.details.total_value)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Line Items</div>
                      <div className="font-medium">{selectedEvent.details.line_item_count}</div>
                    </div>
                  </div>
                )}
                
                {selectedEvent.event_type === 'part_request' && (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {selectedEvent.details.warehouse_name && (
                      <div>
                        <div className="text-muted-foreground">Warehouse</div>
                        <div className="font-medium">{selectedEvent.details.warehouse_name}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-muted-foreground">Line Items</div>
                      <div className="font-medium">{selectedEvent.details.line_item_count}</div>
                    </div>
                    {selectedEvent.details.estimated_total > 0 && (
                      <div>
                        <div className="text-muted-foreground">Estimated Total</div>
                        <div className="font-medium">{formatCurrency(selectedEvent.details.estimated_total)}</div>
                      </div>
                    )}
                    {selectedEvent.details.approved_at && (
                      <div>
                        <div className="text-muted-foreground">Approved At</div>
                        <div className="font-medium">
                          {formatLocalTime(selectedEvent.details.approved_at, 'MMM d, yyyy')}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {selectedEvent.event_type === 'quarantine' && (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-muted-foreground">Item</div>
                      <div className="font-medium">{selectedEvent.details.item_name}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Quantity</div>
                      <div className="font-medium">{selectedEvent.details.quantity}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Reason</div>
                      <div className="font-medium capitalize">
                        {selectedEvent.details.reason?.replace(/_/g, ' ')}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Location</div>
                      <div className="font-medium">{selectedEvent.details.location_name}</div>
                    </div>
                    {selectedEvent.details.resolution_action && (
                      <div className="col-span-2">
                        <div className="text-muted-foreground">Resolution</div>
                        <div className="font-medium capitalize">
                          {selectedEvent.details.resolution_action.replace(/_/g, ' ')}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Notes */}
                {selectedEvent.details.notes && (
                  <div className="mt-4">
                    <div className="text-muted-foreground text-sm mb-1">Notes</div>
                    <div className="bg-muted/30 rounded p-3 text-sm">{selectedEvent.details.notes}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default JobTimeline;
